import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useGoogleWorkspaceConnected } from '../../hooks/useGoogleWorkspaceConnected';
import toast from 'react-hot-toast';
import { Button } from '@/components';
import { parseApiError } from '@/common/utils';
import { uploadQuotationToDrive, formatDriveUploadError } from '../../api';
import {
  useDownloadQuotationPdf,
  useOpenQuotationPdf,
  useRegenerateQuotationPdf,
} from '../hooks';
import { quotationPdfFilename, getCompanyId } from '../utils';
import { DownloadIcon } from '../../components/Icons';

/**
 * Preview / Download / Regenerate — always uses backend PDF endpoints.
 * Never generates a catalog PDF in the browser.
 */
export function QuotationPdfActions({
  companyId,
  quotation,
  productCount,
  canEdit = false,
  compact = false,
  hideHeader = false,
  className = '',
}) {
  const openMutation = useOpenQuotationPdf();
  const downloadMutation = useDownloadQuotationPdf();
  const regenerateMutation = useRegenerateQuotationPdf();

  const effectiveCompanyId = companyId || getCompanyId();
  const googleDrive = useGoogleWorkspaceConnected(effectiveCompanyId);
  const [isUploadingToDrive, setIsUploadingToDrive] = useState(false);
  const { mutate: uploadToDrive } = useMutation({
    mutationFn: () =>
      uploadQuotationToDrive({
        companyId: effectiveCompanyId,
        quotationId: quotation?.id,
      }),
    onMutate: () => setIsUploadingToDrive(true),
    onSuccess: (data) => {
      setIsUploadingToDrive(false);
      if (data?.webViewLink) {
        toast.success(
          <span>
            Uploaded to Drive!{' '}
            <a
              href={data.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              View in Drive
            </a>
          </span>,
          { duration: 6000 },
        );
      } else {
        toast.success('Quotation uploaded to Google Drive successfully.');
      }
    },
    onError: (err) => {
      setIsUploadingToDrive(false);
      toast.error(
        formatDriveUploadError(err, 'Failed to upload quotation to Drive.'),
      );
    },
  });

  if (!quotation?.id) return null;

  const pdfReady = Boolean(quotation.pdf_ready);
  const count =
    productCount ??
    (Array.isArray(quotation.products) ? quotation.products.length : 0);
  const showRegenerate = canEdit && (pdfReady || count > 0);
  const busy =
    openMutation.isPending ||
    downloadMutation.isPending ||
    regenerateMutation.isPending ||
    isUploadingToDrive;

  const pdfNotReadyMessage = (status) => {
    if (status === 404) return 'PDF has not been generated yet.';
    return null;
  };

  const onPreview = (e) => {
    e?.stopPropagation?.();
    if (!pdfReady) {
      toast.error('Add at least one available product to generate the PDF.');
      return;
    }
    openMutation.mutate(
      { companyId, quotationId: quotation.id },
      {
        onError: (err) => {
          toast.error(
            pdfNotReadyMessage(err?.response?.status) ||
              parseApiError(err).message ||
              'Failed to open PDF.',
          );
        },
      },
    );
  };

  const onDownload = (e) => {
    e?.stopPropagation?.();
    if (!pdfReady) {
      toast.error('PDF has not been generated yet.');
      return;
    }
    downloadMutation.mutate(
      {
        companyId,
        quotationId: quotation.id,
        filename: quotationPdfFilename(quotation),
      },
      {
        onSuccess: () => toast.success('PDF downloaded.'),
        onError: (err) => {
          toast.error(
            pdfNotReadyMessage(err?.response?.status) ||
              parseApiError(err).message ||
              'Failed to download PDF.',
          );
        },
      },
    );
  };

  const onRegenerate = (e) => {
    e?.stopPropagation?.();
    regenerateMutation.mutate(
      { companyId, quotationId: quotation.id },
      {
        onSuccess: (data) => {
          if (data?.pdf_ready) toast.success('PDF regenerated.');
          else toast.error('Add at least one available product.');
        },
        onError: (err) => {
          toast.error(
            parseApiError(err).message || 'Failed to regenerate PDF.',
          );
        },
      },
    );
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <button
          type="button"
          disabled={!pdfReady || busy}
          onClick={onPreview}
          title={pdfReady ? 'Preview PDF' : 'PDF not ready'}
          className="p-1.5 rounded-full hover:bg-primary/10 text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <svg
            className="w-[18px] h-[18px]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="13" y2="17" />
          </svg>
        </button>
        <button
          type="button"
          disabled={!pdfReady || busy}
          onClick={onDownload}
          title={pdfReady ? 'Download PDF' : 'PDF not ready'}
          className="p-1.5 rounded-full hover:bg-primary/10 text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <DownloadIcon className="w-[18px] h-[18px]" />
        </button>
        {googleDrive && (
          <button
            type="button"
            disabled={isUploadingToDrive}
            onClick={(e) => {
              e?.stopPropagation?.();
              uploadToDrive();
            }}
            title={
              isUploadingToDrive
                ? 'Uploading to Google Drive...'
                : 'Upload to Google Drive'
            }
            className="p-1.5 rounded-full hover:bg-yellow-50 text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Upload to Google Drive"
          >
            {isUploadingToDrive ? (
              <div className="w-[18px] h-[18px] border-2 border-yellow-400/30 border-t-yellow-500 rounded-full animate-spin" />
            ) : (
              <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]">
                <path fill="#FBBC05" d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z" />
                <path fill="#4285F4" d="M22.85 15L16.29 3.5H9.15L15.71 15z" />
                <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
              </svg>
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {!hideHeader && (
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
              Catalog PDF
            </p>
            <p className="text-[11px] text-text-muted mt-0.5">
              {pdfReady
                ? quotation.pdf_generated_at
                  ? `Ready · ${new Date(quotation.pdf_generated_at).toLocaleString('en-IN')}`
                  : 'Ready for WhatsApp'
                : count > 0
                  ? 'PDF not ready — try Regenerate'
                  : 'Add at least one available product'}
            </p>
          </div>
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              pdfReady
                ? 'bg-surface-mint text-primary'
                : 'bg-[#F5F5F5] text-text-muted'
            }`}
          >
            {busy && regenerateMutation.isPending
              ? 'Updating…'
              : pdfReady
                ? 'Ready'
                : 'Not ready'}
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 w-full">
        <Button
          type="button"
          variant="secondary"
          disabled={!pdfReady || busy}
          onClick={onPreview}
          className="h-8 sm:h-9 !px-2 !text-xs w-full justify-center whitespace-nowrap"
        >
          {openMutation.isPending ? 'Opening…' : 'Preview'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!pdfReady || busy}
          onClick={onDownload}
          className="h-8 sm:h-9 !px-2 !text-xs w-full justify-center whitespace-nowrap"
          icon={<DownloadIcon className="w-3.5 h-3.5" />}
          iconPosition="start"
        >
          {downloadMutation.isPending ? 'Downloading…' : 'Download'}
        </Button>
        {googleDrive && (
          <Button
            type="button"
            variant="secondary"
            disabled={isUploadingToDrive}
            onClick={(e) => {
              e?.stopPropagation?.();
              uploadToDrive();
            }}
            className={`h-8 sm:h-9 !px-2 !text-xs w-full justify-center whitespace-nowrap ${!showRegenerate ? 'col-span-2' : ''}`}
            icon={
              isUploadingToDrive ? (
                <div className="w-3.5 h-3.5 border-2 border-yellow-400/30 border-t-yellow-500 rounded-full animate-spin" />
              ) : (
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
                  <path
                    fill="#FBBC05"
                    d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z"
                  />
                  <path fill="#4285F4" d="M22.85 15L16.29 3.5H9.15L15.71 15z" />
                  <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
                </svg>
              )
            }
            iconPosition="start"
          >
            {isUploadingToDrive ? 'Uploading…' : 'Drive'}
          </Button>
        )}
        {showRegenerate && (
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={onRegenerate}
            className="h-8 sm:h-9 !px-2 !text-xs w-full justify-center whitespace-nowrap"
          >
            {regenerateMutation.isPending ? 'Regenerating…' : 'Regenerate'}
          </Button>
        )}
      </div>
    </div>
  );
}
